import {
    Observable
} from "rxjs";
import {
    map
} from "rxjs/operators";

import {
    Injectable, inject
} from "@angular/core";

import {
    ModelAction, ISearchSort
} from "@shopliftr/common-js/data";
import { InvalidParametersException } from "../../../../exceptions/invalid-parameters-exception";
import {
    RequestUtil,
    CustomOpResponse,
    BulkUtil,
    BulkResponse,
    BulkResponseItem,
    SearchResponse,
    CreateRequest,
    GetRequest,
    UpdateRequest
} from "@shopliftr/common-js/shared";
import {
    AdUnit,
    AdUnitOperation,
    AdUnitRequestOptions,
    AdUnitSearchFilterOptions,
    AdUnitStatus
} from "@shopliftr/common-js/smart";
import {
    ICreateInternalApiClientService,
    IUpdateInternalApiClientService,
    InternalApiRequestService,
    IQueryInternalApiClientService,
    ListUtil
} from "@shopliftr/common-ng";

/**
 * API Client Service for AdUnit model CRUD operations.
 *
 * Provides comprehensive ad unit management including create, read, update, search,
 * archive, restore, and publish operations with support for bulk actions.
 *
 * **Service Pattern:** API Client Service
 *
 * **Provision:** Singleton service provided in 'root'
 *
 * @implements {ICreateInternalApiClientService<AdUnit>} Standard internal API create client pattern
 * @implements {IUpdateInternalApiClientService<AdUnit>} Standard internal API update client pattern
 * @implements {IQueryInternalApiClientService<AdUnit>} Standard internal API query client pattern
 *
 * @providedIn root
 */
@Injectable({
    providedIn: "root"
})
export class AdUnitApiClientService implements ICreateInternalApiClientService<AdUnit>, IUpdateInternalApiClientService<AdUnit>,
IQueryInternalApiClientService<AdUnit> {
    readonly basePath = "ad-unit";

    private readonly _internalApiRequestService = inject(InternalApiRequestService);

    /**
     * Searches for ad units with given filter options.
     *
     * @param filterOptions - Search filter criteria
     * @param page - Page number for pagination
     * @param pageSize - Number of items per page
     * @param sort - Sort configuration
     * @returns Observable of search response with ad units and metadata
     * @see InternalApiRequestService.search
     */
    search$(
        filterOptions: AdUnitSearchFilterOptions,
        page?: number,
        pageSize?: number,
        sort?: Array<ISearchSort>
    ): Observable<SearchResponse<AdUnit>> {

        const searchRequest = RequestUtil.createSearchRequest(
            ModelAction.ClientSearch,
            AdUnit.modelName,
            ListUtil.getFromValue(page, pageSize),
            pageSize,
            filterOptions,
            sort
        );

        return this._internalApiRequestService.search$(searchRequest, this.basePath);
    }

    /**
     * Retrieves multiple ad units by their IDs in a single bulk request.
     *
     * @param ids - Array of ad unit IDs to retrieve
     * @returns Observable of bulk response containing the requested ad units
     * @see InternalApiRequestService.bulk
     */
    bulkGet$(ids: Array<string>): Observable<BulkResponse> {
        const bulkRequest = BulkUtil.createBulkRequest(ids.map((id: string) => {
            const request = RequestUtil.createGetRequest<AdUnit>(id, AdUnit.modelName, ModelAction.ClientGet);
            return BulkUtil.createRequestItem(request);
        }));
        return this._internalApiRequestService.bulk$(bulkRequest);
    }

    /**
     * Creates multiple ad units in a single bulk request.
     *
     * @param items - Array of create requests for ad units
     * @returns Observable of bulk response containing the created ad units
     * @see InternalApiRequestService.bulk
     */
    bulkCreate$(items: Array<CreateRequest<AdUnit, unknown>>): Observable<BulkResponse> {
        const bulkRequest = BulkUtil.createBulkRequest(
            items.map((item) => BulkUtil.createRequestItem(item))
        );
        return this._internalApiRequestService.bulk$(bulkRequest);
    }

    /**
     * Updates multiple ad units in a single bulk request.
     *
     * @param requests - Array of update requests for ad units
     * @returns Observable of bulk response containing the updated ad units
     * @see InternalApiRequestService.bulk
     */
    bulkUpdate$(requests: Array<UpdateRequest<unknown, unknown>>): Observable<BulkResponse> {
        const bulkRequest = BulkUtil.createBulkRequest(
            requests.map((request) => BulkUtil.createRequestItem(request))
        );
        return this._internalApiRequestService.bulk$(bulkRequest);
    }

    /**
     * Archives the given ad unit using custom operation request.
     *
     * @param adUnit - Ad unit to archive
     * @returns Observable that completes when operation finishes
     * @throws InvalidParametersException if adUnit is not provided
     * @see InternalApiRequestService.customOp
     */
    archive$(adUnit: AdUnit): Observable<void> {

        if (!adUnit) {
            throw new InvalidParametersException("archived ad unit is required");
        }

        const request = RequestUtil.createCustomOpRequest(
            adUnit.id,
            AdUnit.modelName,
            AdUnitOperation.Archive
        );

        return this._internalApiRequestService.customOp$(request, this.basePath).pipe(
            map(() => {})
        );
    }

    /**
     * Bulk archives multiple ad units.
     *
     * @param adUnits - Array of ad units to archive
     * @returns Observable of bulk response items with operation results
     * @see InternalApiRequestService.bulk
     */
    bulkArchive$(adUnits: Array<AdUnit>): Observable<Array<BulkResponseItem<CustomOpResponse<AdUnit>>>> {

        const requests = adUnits.map((adUnit: AdUnit) => {

            const customOpRequest = RequestUtil.createCustomOpRequest(
                adUnit.id,
                AdUnit.modelName,
                AdUnitOperation.Archive
            );
            return BulkUtil.createRequestItem(customOpRequest);
        });
        const bulkRequest = BulkUtil.createBulkRequest(requests);

        return this._internalApiRequestService.bulk$(bulkRequest).pipe(
            map((response) => response.results as Array<BulkResponseItem<CustomOpResponse<AdUnit>>>)
        );
    }

    /**
     * Bulk publishes multiple ad units to production.
     *
     * @param adUnits - Array of ad units to publish
     * @returns Observable of bulk response items with operation results
     * @see InternalApiRequestService.bulk
     */
    bulkPublish$(adUnits: Array<AdUnit>): Observable<Array<BulkResponseItem<CustomOpResponse<AdUnit>>>> {

        const requests = adUnits.map((adUnit: AdUnit) => {

            const customOpRequest = RequestUtil.createCustomOpRequest(
                adUnit.id,
                AdUnit.modelName,
                AdUnitOperation.Publish
            );
            return BulkUtil.createRequestItem(customOpRequest);
        });
        const bulkRequest = BulkUtil.createBulkRequest(requests);

        return this._internalApiRequestService.bulk$(bulkRequest).pipe(
            map((response) => response.results as Array<BulkResponseItem<CustomOpResponse<AdUnit>>>)
        );
    }

    /**
     * Bulk restores multiple archived ad units.
     *
     * @param adUnits - Array of ad units to restore
     * @returns Observable of bulk response items with operation results
     * @see InternalApiRequestService.bulk
     */
    bulkRestore$(adUnits: Array<AdUnit>): Observable<Array<BulkResponseItem<CustomOpResponse<AdUnit>>>> {

        const requests = adUnits.map((adUnit: AdUnit) => {

            const customOpRequest = RequestUtil.createCustomOpRequest(
                adUnit.id,
                AdUnit.modelName,
                AdUnitOperation.Restore
            );
            return BulkUtil.createRequestItem(customOpRequest);
        });
        const bulkRequest = BulkUtil.createBulkRequest(requests);

        return this._internalApiRequestService.bulk$(bulkRequest).pipe(
            map((response) => response.results as Array<BulkResponseItem<CustomOpResponse<AdUnit>>>)
        );
    }

    /**
     * Creates a new ad unit with InDevelopment status.
     *
     * @param adUnit - Ad unit to create
     * @returns Observable of created ad unit
     */
    create$(adUnit: AdUnit): Observable<AdUnit> {

        const options = new AdUnitRequestOptions().setDefaults();
        const payload = adUnit.cloneForAction(ModelAction.ClientCreate);
        payload.status = AdUnitStatus.InDevelopment;
        const createRequest: CreateRequest<AdUnit, AdUnitRequestOptions> = RequestUtil.createCreateRequest(
            ModelAction.ClientCreate, payload, options
        );
        return this._internalApiRequestService.create$(createRequest, this.basePath).pipe(map((response) => response.payload));
    }

    /**
     * Retrieves a single ad unit by ID.
     *
     * @param adUnitId - ID of the ad unit to retrieve
     * @returns Observable of the ad unit
     * @see InternalApiRequestService.get
     */
    get$(adUnitId: string): Observable<AdUnit> {

        const getRequest: GetRequest<AdUnit> = RequestUtil.createGetRequest(
            adUnitId,
            AdUnit.modelName,
            ModelAction.ClientGet
        );
        return this._internalApiRequestService.get$(getRequest, this.basePath).pipe(map((response) => response.payload as AdUnit));
    }

    /**
     * Retrieves all ad units matching the search criteria.
     *
     * @param filterOptions - Search filter criteria
     * @returns Observable of ad unit array
     */
    getAll$(filterOptions: AdUnitSearchFilterOptions): Observable<Array<AdUnit>> {

        const searchRequest = RequestUtil.createSearchRequest(
            ModelAction.ClientSearch,
            AdUnit.modelName,
            0,
            100,
            filterOptions
        );

        return this._internalApiRequestService.getAll$(searchRequest, this.basePath);
    }

    /**
     * Publishes the given ad unit to production.
     *
     * @param adUnit - Ad unit to publish
     * @returns Observable that completes when operation finishes
     * @throws InvalidParametersException if adUnit is not provided
     */
    publish$(adUnit: AdUnit): Observable<void> {

        if (!adUnit) {
            throw new InvalidParametersException("published ad unit is required");
        }

        const request = RequestUtil.createCustomOpRequest(
            adUnit.id,
            AdUnit.modelName,
            AdUnitOperation.Publish
        );

        return this._internalApiRequestService.customOp$(request, this.basePath).pipe(
            map(() => {})
        );
    }

    /**
     * Restores the given archived ad unit.
     *
     * @param adUnit - Ad unit to restore
     * @returns Observable that completes when operation finishes
     * @throws InvalidParametersException if adUnit is not provided
     * @see InternalApiRequestService.customOp
     */
    restore$(adUnit: AdUnit): Observable<void> {

        if (!adUnit) {
            throw new InvalidParametersException("restored ad unit is required");
        }

        const request = RequestUtil.createCustomOpRequest(
            adUnit.id,
            AdUnit.modelName,
            AdUnitOperation.Restore
        );

        return this._internalApiRequestService.customOp$(request, this.basePath).pipe(
            map(() => {})
        );
    }

    /**
     * Updates an ad unit with specified fields.
     *
     * @param id - ID of the ad unit to update
     * @param version - Current version number for optimistic locking
     * @param fields - Ad unit fields to update
     * @param removeFields - Array of field names to remove
     * @returns Observable of updated ad unit
     * @see InternalApiRequestService.update
     */
    update$(id: string, version: number, fields: AdUnit, removeFields: Array<string>): Observable<AdUnit> {

        const updateFields = fields.cloneForAction(ModelAction.ClientUpdate);
        const updateRequest: UpdateRequest<AdUnit> = RequestUtil.createUpdateRequest(
            id, ModelAction.ClientUpdate, updateFields, version, removeFields
        );
        return this._internalApiRequestService.update$(updateRequest, this.basePath).pipe(map((response) => response.payload));
    }

    /**
     * Updates an ad unit by comparing original and updated models.
     *
     * @param original - Original ad unit model
     * @param updated - Updated ad unit model with changes
     * @returns Observable of updated ad unit
     */
    updateWithChanges$(original: AdUnit, updated: AdUnit): Observable<AdUnit> {

        return this._internalApiRequestService.updateWithChanges$(original, updated, this.basePath);
    }
}
